import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, AlertOctagon, Link2, Pencil, Trash2, History } from "lucide-react";
import api, { formatApiError } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { formatPrice, formatNumber, timeLeft, formatPct } from "../../lib/format";
import SnapshotTimeline from "./SnapshotTimeline";
import OnChainModal from "./OnChainModal";

export default function PositionsPanel({ refreshKey }) {
    const { user } = useAuth();
    const [data, setData] = useState({ orders: [], swaps: [] });
    const [, setTick] = useState(0);
    const [expandedId, setExpandedId] = useState(null);
    const [exitConfirmId, setExitConfirmId] = useState(null);
    const [addMarginId, setAddMarginId] = useState(null);
    const [editOrderId, setEditOrderId] = useState(null);
    const [deleteOrderId, setDeleteOrderId] = useState(null);
    const [lineageOrderId, setLineageOrderId] = useState(null);
    const [editPositionId, setEditPositionId] = useState(null);
    const [onchainSwap, setOnchainSwap] = useState(null); // { id, coin, settled }

    const load = async () => {
        try {
            const r = await api.get("/swaps/my");
            setData(r.data);
        } catch {}
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 3000);
        const idT = setInterval(() => setTick((t) => t + 1), 1000);
        return () => { clearInterval(id); clearInterval(idT); };
    }, [refreshKey]);

    // Terminal-grade live-only surface. Closed trades live on /positions via
    // <ClosedPositionsPanel/>. We deliberately do NOT filter or fetch SETTLED
    // swaps here so a stray render path can never resurrect them.
    const open = (data.orders || []).filter((o) => o.status === "OPEN" || o.status === "LIMIT_PENDING");
    const active = (data.swaps || []).filter((s) => s.state === "ACTIVE");

    const deleteOrderConfirmed = async (id) => {
        try {
            await api.delete(`/swaps/orders/${id}`);
            toast.success("Order deleted", { description: "Margin returned to free balance." });
            setDeleteOrderId(null);
            load();
        } catch (e) {
            toast.error("Delete failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    const editOrderConfirmed = async (id, patch) => {
        try {
            const { data: o } = await api.patch(`/swaps/orders/${id}`, patch);
            toast.success("Order updated", {
                description: `${o.side} ${o.coin} · ${o.notional.toLocaleString()} notional · ${o.order_type === "LIMIT" ? `LIMIT @ ${o.limit_price}` : "MARKET"}`,
            });
            setEditOrderId(null);
            load();
        } catch (e) {
            toast.error("Edit failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    const confirmExit = async (id) => {
        try {
            const { data } = await api.post(`/swaps/${id}/exit`);
            toast.success("Position closed early", {
                description: `PnL settled at oracle price. Penalty: ${(data.exit_fee_to_counter + data.exit_fee_to_protocol).toFixed(2)} USDC`,
            });
            setExitConfirmId(null);
            load();
        } catch (e) {
            toast.error("Close failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    const confirmAddMargin = async (id, amount) => {
        try {
            await api.post(`/swaps/${id}/margin`, { amount: parseFloat(amount) });
            toast.success(`Added ${parseFloat(amount).toFixed(2)} USDC variable margin`);
            setAddMarginId(null);
            load();
        } catch (e) {
            toast.error("Add margin failed", { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    const editPosition = async (id, action, payload) => {
        try {
            if (action === "add") {
                await api.post(`/swaps/${id}/margin`, { amount: parseFloat(payload.amount) });
                toast.success(`Added ${parseFloat(payload.amount).toFixed(2)} USDC margin`);
            } else if (action === "reduce") {
                await api.post(`/swaps/${id}/margin/reduce`, { amount: parseFloat(payload.amount) });
                toast.success(`Reduced ${parseFloat(payload.amount).toFixed(2)} USDC margin back to free`);
            } else if (action === "partial") {
                const { data } = await api.post(`/swaps/${id}/partial-close`, { fraction: parseFloat(payload.fraction) });
                toast.success(`Closed ${(payload.fraction * 100).toFixed(0)}% of position`, {
                    description: `Realised PnL: ${data.realised_pnl > 0 ? "+" : ""}${data.realised_pnl.toFixed(2)} USDC`,
                });
            }
            setEditPositionId(null);
            load();
        } catch (e) {
            toast.error(`${action === "reduce" ? "Reduce" : action === "partial" ? "Partial close" : "Add margin"} failed`,
                { description: formatApiError(e.response?.data?.detail) || e.message });
        }
    };

    return (
        <div className="bg-[#0A0A0C] border border-[#1F1F24] rounded-sm" data-testid="positions-panel">
            <div className="px-4 py-3 border-b border-[#1F1F24] flex items-baseline justify-between">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">My Positions</div>
                    <div className="text-sm font-medium font-mono tracking-tight mt-0.5">
                        {active.length} active · {open.length} open <span className="text-zinc-600">· closed trades on the <a className="text-cyan-400 hover:text-cyan-300 underline" href="/positions">Positions</a> page</span>
                    </div>
                </div>
            </div>

            <div className="p-3 grid gap-4">
                {open.length > 0 && (
                    <Section title="Open orders (awaiting match)">
                        <Table headers={["Side", "Coin", "Notional", "Margin", "Duration", "Type", ""]}>
                            {open.map((o) => (
                                <tr key={o.id} className="border-t border-[#1F1F24]">
                                    <td className={`py-1.5 px-2 font-mono text-xs ${o.side === "BULL" ? "text-emerald-400" : "text-rose-400"}`}>{o.side}</td>
                                    <td className="py-1.5 px-2 font-mono text-xs">{o.coin}{o.cross_coin ? `/${o.cross_coin}` : ""}</td>
                                    <td className="py-1.5 px-2 font-mono text-xs">{o.notional.toLocaleString()}</td>
                                    <td className="py-1.5 px-2 font-mono text-xs">{o.margin.toLocaleString()}</td>
                                    <td className="py-1.5 px-2 font-mono text-xs">{o.duration_key}</td>
                                    <td className="py-1.5 px-2 font-mono text-[10px] text-zinc-400">
                                        {o.order_type || "MARKET"}{o.limit_price ? ` @ ${o.limit_price}` : ""}
                                    </td>
                                    <td className="py-1.5 px-2 text-right whitespace-nowrap">
                                        {(o.revision && o.revision > 1) || o.replaced_by || o.replaced_from ? (
                                            <button
                                                data-testid={`history-order-${o.id}`}
                                                onClick={() => setLineageOrderId(o.id)}
                                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-cyan-300 mr-3"
                                                title="View this order's edit history"
                                            >
                                                <History className="w-3 h-3" /> history
                                            </button>
                                        ) : null}
                                        <button
                                            data-testid={`edit-order-${o.id}`}
                                            onClick={() => setEditOrderId(o.id)}
                                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300 mr-3"
                                        >
                                            <Pencil className="w-3 h-3" /> edit
                                        </button>
                                        <button
                                            data-testid={`delete-order-${o.id}`}
                                            onClick={() => setDeleteOrderId(o.id)}
                                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-rose-400 hover:text-rose-300"
                                        >
                                            <Trash2 className="w-3 h-3" /> delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </Table>
                    </Section>
                )}

                {active.length > 0 && (
                    <Section title="Active swaps (mark-to-market · timeline updates every minute)">
                        <div className="grid gap-2">
                            {active.map((s) => (
                                <ActiveSwapCard
                                    key={s.id}
                                    s={s}
                                    userId={user?.id}
                                    expanded={expandedId === s.id}
                                    onToggleExpand={() => setExpandedId(expandedId === s.id ? null : s.id)}
                                    onExitClick={() => setExitConfirmId(s.id)}
                                    onEditClick={() => setEditPositionId(s.id)}
                                    onAddMarginClick={() => setAddMarginId(s.id)}
                                    onViewOnChain={() => setOnchainSwap({ id: s.id, coin: s.coin, settled: false })}
                                />
                            ))}
                        </div>
                    </Section>
                )}

                {/* Closed/settled trades intentionally NOT rendered here — they
                    live exclusively on /positions via <ClosedPositionsPanel/>. This
                    keeps the terminal a live-only surface and makes regressions
                    impossible regardless of any future prop changes. */}

                {open.length === 0 && active.length === 0 && (
                    <div className="text-center py-8 text-[11px] font-mono text-zinc-600">No live activity. Place your first BUBE swap from the ticket, or view your closed trades on the <a className="text-cyan-400 hover:text-cyan-300 underline" href="/positions">Positions</a> page.</div>
                )}
            </div>

            {exitConfirmId && (
                <ExitConfirmModal
                    swap={active.find((s) => s.id === exitConfirmId)}
                    userId={user?.id}
                    onCancel={() => setExitConfirmId(null)}
                    onConfirm={() => confirmExit(exitConfirmId)}
                />
            )}
            {addMarginId && (
                <AddMarginModal
                    swap={active.find((s) => s.id === addMarginId)}
                    userId={user?.id}
                    onCancel={() => setAddMarginId(null)}
                    onConfirm={(amt) => confirmAddMargin(addMarginId, amt)}
                />
            )}
            {editOrderId && (
                <EditOrderModal
                    order={open.find((o) => o.id === editOrderId)}
                    onCancel={() => setEditOrderId(null)}
                    onConfirm={(patch) => editOrderConfirmed(editOrderId, patch)}
                />
            )}
            {deleteOrderId && (
                <DeleteOrderModal
                    order={open.find((o) => o.id === deleteOrderId)}
                    onCancel={() => setDeleteOrderId(null)}
                    onConfirm={() => deleteOrderConfirmed(deleteOrderId)}
                />
            )}
            {editPositionId && (
                <EditPositionModal
                    swap={active.find((s) => s.id === editPositionId)}
                    userId={user?.id}
                    onCancel={() => setEditPositionId(null)}
                    onSubmit={(action, payload) => editPosition(editPositionId, action, payload)}
                />
            )}
            {lineageOrderId && (
                <OrderLineageModal
                    orderId={lineageOrderId}
                    onClose={() => setLineageOrderId(null)}
                />
            )}
            {onchainSwap && (
                <OnChainModal
                    swapId={onchainSwap.id}
                    coin={onchainSwap.coin}
                    settled={!!onchainSwap.settled}
                    onClose={() => setOnchainSwap(null)}
                />
            )}
        </div>
    );
}

function ActiveSwapCard({ s, userId, expanded, onToggleExpand, onExitClick, onEditClick, onAddMarginClick, onViewOnChain }) {
    const userSide = s.user_side || (s.bull_user_id === userId ? "BULL" : "BEAR");
    const userPnl = userSide === "BULL" ? s.bull_pnl : s.bear_pnl;
    const counterPnl = userSide === "BULL" ? s.bear_pnl : s.bull_pnl;
    const userMargin = userSide === "BULL" ? s.margin_bull : s.margin_bear;
    const userInitial = userSide === "BULL" ? (s.initial_margin_bull ?? s.margin_bull) : (s.initial_margin_bear ?? s.margin_bear);
    const userVariable = Math.max(0, userMargin - userInitial);
    const counterMargin = userSide === "BULL" ? s.margin_bear : s.margin_bull;
    const volatility = s.current_price && s.entry_price ? ((s.current_price - s.entry_price) / s.entry_price) * 100 : 0;
    const userPnlPos = (userPnl || 0) >= 0;

    // Threshold = % of user's margin still intact
    const threshold = userMargin > 0 ? Math.max(0, Math.min(100, ((userMargin + (userPnl || 0)) / userMargin) * 100)) : 0;
    const tWarn = threshold < 5 ? { level: "auto", msg: "Position will auto-close at 5% margin remaining (95% used)." } :
                  threshold < 10 ? { level: "strong", msg: "Strong warning — over 90% of your margin is consumed. Add margin or close." } :
                  threshold < 20 ? { level: "warn", msg: "Warning — over 80% of your margin is consumed. Consider adding variable margin." } : null;

    return (
        <div className="border border-[#1F1F24] rounded-sm bg-[#050505]/60" data-testid={`active-swap-${s.id}`}>
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2.5 border-b border-[#1F1F24]/60">
                <span className={`text-[10px] font-mono font-semibold uppercase tracking-widest px-2 py-1 rounded-sm ${userSide === "BULL" ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                    Your side · {userSide}
                </span>
                <div className="flex items-center gap-4 font-mono text-xs flex-wrap">
                    <span className="text-zinc-400">
                        {s.coin}
                        {s.swap_type === "basket" && (
                            <span data-testid={`basket-tag-${s.id}`} className="ml-1.5 text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">basket</span>
                        )}
                        {s.swap_type === "basket-vs-basket" && (
                            <span data-testid={`basket-vb-tag-${s.id}`} className="ml-1.5 text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-violet-500/40 bg-violet-500/10 text-violet-300">basket-vs-basket</span>
                        )}
                        {s.notional_mode === "dynamic" && (
                            <span data-testid={`notional-mode-tag-${s.id}`} className="ml-1.5 text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300" title="Notional rebases every 24h">dynamic</span>
                        )}
                    </span>
                    {s.via_bot && (
                        <span data-testid={`via-bot-${s.id}`} className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-violet-500/40 bg-violet-500/10 text-violet-300">
                            via {s.via_bot}
                        </span>
                    )}
                    <span className="text-zinc-500">
                        Entry <span className="text-zinc-200">{formatPrice(s.entry_price)}</span>
                    </span>
                    <span className="text-zinc-500">
                        {(s.swap_type === "basket" || s.swap_type === "basket-vs-basket") ? "Level" : "Now"} <span className="text-cyan-400">{s.current_price ? formatPrice(s.current_price) : "—"}</span>
                    </span>
                    <span className={`${volatility >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{formatPct(volatility, 3)}</span>
                    <span className="text-zinc-500 hidden md:inline">Left <span className="text-zinc-300">{timeLeft(s.expiry_unix)}</span></span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid={`edit-position-${s.id}`}
                        onClick={onEditClick}
                        className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300 border border-cyan-500/30 hover:border-cyan-400 px-2.5 py-1 rounded-sm"
                    >
                        <Pencil className="w-3 h-3" /> Edit position
                    </button>
                    <button
                        data-testid={`viewonchain-swap-${s.id}`}
                        onClick={onViewOnChain}
                        title="View this swap's on-chain attestations on Hyperliquid testnet — free, zero cost"
                        className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-400 px-2.5 py-1 rounded-sm"
                    >
                        <Link2 className="w-3 h-3" /> View on-chain
                    </button>
                    <button
                        data-testid={`expand-swap-${s.id}`}
                        onClick={onToggleExpand}
                        className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-cyan-400 px-2 py-1"
                    >
                        {expanded ? "Hide history" : "Show history"}
                    </button>
                    <button
                        data-testid={`exit-swap-${s.id}`}
                        onClick={onExitClick}
                        className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-rose-400 hover:text-rose-300 border border-rose-500/30 hover:border-rose-400 px-2.5 py-1 rounded-sm"
                    >
                        <Trash2 className="w-3 h-3" /> Close & delete
                    </button>
                </div>
            </div>

            {tWarn && (
                <div
                    className={`px-3 py-2 border-b flex items-center gap-2 ${
                        tWarn.level === "auto" ? "bg-rose-500/15 border-rose-500/40" :
                        tWarn.level === "strong" ? "bg-orange-500/10 border-orange-500/40" :
                        "bg-amber-500/10 border-amber-500/40"
                    }`}
                    data-testid={`threshold-warning-${s.id}`}
                >
                    {tWarn.level === "auto" ? <AlertOctagon className="w-3.5 h-3.5 text-rose-400" /> : <AlertTriangle className={`w-3.5 h-3.5 ${tWarn.level === "strong" ? "text-orange-400" : "text-amber-300"}`} />}
                    <div className={`text-[10px] font-mono tracking-tight ${
                        tWarn.level === "auto" ? "text-rose-200" : tWarn.level === "strong" ? "text-orange-200" : "text-amber-200"
                    }`}>
                        Threshold {threshold.toFixed(1)}% · {tWarn.msg}
                    </div>
                </div>
            )}

            {(s.swap_type === "basket" || s.swap_type === "basket-vs-basket") && s.basket && (
                <div className="px-3 py-2 border-b border-[#1F1F24]/60 bg-[#050507]" data-testid={`basket-composition-${s.id}`}>
                    <div className="flex items-center flex-wrap gap-1.5">
                        <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500 mr-1">
                            {s.swap_type === "basket-vs-basket" ? "Leg A" : "Composition"}:
                        </span>
                        {s.basket.constituents.map((c) => (
                            <span key={c.coin} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-cyan-500/30 bg-cyan-500/5 text-cyan-200">
                                {c.coin} <span className="text-zinc-500">{(c.weight_pct * 100).toFixed(1)}%</span>
                            </span>
                        ))}
                    </div>
                    {s.swap_type === "basket-vs-basket" && s.basket_b && (
                        <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                            <span className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500 mr-1">Leg B:</span>
                            {s.basket_b.constituents.map((c) => (
                                <span key={c.coin} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm border border-violet-500/30 bg-violet-500/5 text-violet-200">
                                    {c.coin} <span className="text-zinc-500">{(c.weight_pct * 100).toFixed(1)}%</span>
                                </span>
                            ))}
                        </div>
                    )}
                    {s.notional_mode === "dynamic" && (s.basket_rebases?.length || 0) > 0 && (
                        <div className="mt-1.5 text-[9px] font-mono text-amber-300">
                            Rebased {s.basket_rebases.length}× · current notional ${Number(s.notional).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </div>
                    )}
                </div>
            )}

            <div className="grid grid-cols-2 gap-px bg-[#1F1F24]">
                <div className={`px-3 py-3 bg-[#0A0A0C] ${userSide === "BULL" ? "ring-1 ring-inset ring-emerald-500/30" : "ring-1 ring-inset ring-rose-500/30"}`}>
                    <div className="flex items-baseline justify-between">
                        <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">{userSide} P&amp;L · You</div>
                        <div className="text-[9px] font-mono text-zinc-600">
                            margin {formatNumber(userMargin)}
                            {userVariable > 0 && <span className="text-cyan-400"> (+{formatNumber(userVariable)})</span>}
                        </div>
                    </div>
                    <div className={`mt-1 font-mono text-2xl tracking-tighter ${userPnlPos ? "text-emerald-400" : "text-rose-400"}`}>
                        {userPnlPos ? "+" : ""}${formatNumber(Math.abs(userPnl || 0))}
                    </div>
                    <div className="mt-1 text-[9px] font-mono text-zinc-600">Threshold {threshold.toFixed(1)}%</div>
                </div>
                <div className="px-3 py-3 bg-[#0A0A0C]">
                    <div className="flex items-baseline justify-between">
                        <div className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">Counterparty</div>
                        <div className="text-[9px] font-mono text-zinc-600">margin {formatNumber(counterMargin)}</div>
                    </div>
                    <div className={`mt-1 font-mono text-2xl tracking-tighter ${(counterPnl || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                        {(counterPnl || 0) >= 0 ? "+" : ""}${formatNumber(Math.abs(counterPnl || 0))}
                    </div>
                    {userMargin !== counterMargin && (
                        <div className="mt-1 text-[9px] font-mono text-zinc-600">
                            Asymmetric · {userMargin > counterMargin ? "you posted more" : "you posted less"}
                        </div>
                    )}
                </div>
            </div>

            {expanded && <SnapshotTimeline swapId={s.id} userSide={userSide} coin={s.coin} />}
        </div>
    );
}

function SettledSwapRow({ s, userId, expanded, onToggleExpand, onViewOnChain }) {
    const userSide = s.user_side || (s.bull_user_id === userId ? "BULL" : "BEAR");
    const isWinner = s.winner_user_id === userId;
    return (
        <div className="border border-[#1F1F24] rounded-sm bg-[#050505]/60" data-testid={`settled-swap-${s.id}`}>
            <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 px-3 py-2.5">
                <span className={`text-[10px] font-mono font-semibold uppercase tracking-widest px-2 py-1 rounded-sm ${userSide === "BULL" ? "bg-emerald-500/10 text-emerald-400" : "bg-rose-500/10 text-rose-400"}`}>
                    {userSide}
                </span>
                <div className="flex items-center gap-3 sm:gap-4 font-mono text-xs flex-wrap">
                    <span className="text-zinc-300">{s.coin}</span>
                    {s.via_bot && (
                        <span data-testid={`via-bot-settled-${s.id}`} className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-violet-500/40 bg-violet-500/10 text-violet-300">
                            via {s.via_bot}
                        </span>
                    )}
                    <span className="text-zinc-500">Entry <span className="text-zinc-200">{formatPrice(s.entry_price)}</span></span>
                    <span className="text-zinc-500">Exit <span className="text-zinc-200">{formatPrice(s.exit_price)}</span></span>
                    <span className={`${s.outcome === "BULLISH" ? "text-emerald-400" : s.outcome === "BEARISH" ? "text-rose-400" : "text-zinc-400"}`}>{s.outcome}</span>
                    <span className="text-zinc-500">{s.exit_type || "EXPIRY"}</span>
                    <span className={isWinner ? "text-emerald-400" : "text-rose-400"}>
                        {isWinner ? "+" : "−"}${formatNumber(Math.abs(s.pnl || 0))}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid={`viewonchain-settled-${s.id}`}
                        onClick={onViewOnChain}
                        title="View this swap's on-chain attestations on Hyperliquid testnet — free, zero cost"
                        className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-400 px-2.5 py-1 rounded-sm"
                    >
                        <Link2 className="w-3 h-3" /> View on-chain
                    </button>
                    <button
                        data-testid={`expand-settled-${s.id}`}
                        onClick={onToggleExpand}
                        className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-cyan-400 px-2 py-1"
                    >
                        {expanded ? "Hide history" : "Show history"}
                    </button>
                </div>
            </div>
            {expanded && <SnapshotTimeline swapId={s.id} userSide={userSide} coin={s.coin} settled />}
        </div>
    );
}

function ExitConfirmModal({ swap, userId, onCancel, onConfirm }) {
    if (!swap) return null;
    const userSide = swap.user_side || (swap.bull_user_id === userId ? "BULL" : "BEAR");
    const userMargin = userSide === "BULL" ? swap.margin_bull : swap.margin_bear;
    const feeToCounter = userMargin * 0.02;
    const feeToProtocol = userMargin * 0.01;
    const userPnl = userSide === "BULL" ? swap.bull_pnl : swap.bear_pnl;
    const netImpact = (userPnl || 0) - feeToCounter - feeToProtocol;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center px-4" data-testid="exit-confirm-modal">
            <div className="w-full max-w-md bg-[#0A0A0C] border border-[#1F1F24] rounded-sm">
                <div className="px-5 py-4 border-b border-[#1F1F24]">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-amber-400">Early exit · penalty applies</div>
                    <div className="text-lg font-medium mt-1">Close {swap.coin} {userSide} position?</div>
                </div>
                <div className="p-5 grid gap-3 font-mono text-xs">
                    <Row label="Your margin" value={`$${formatNumber(userMargin)}`} />
                    <Row label="Current PnL (MtM)" value={`${(userPnl || 0) >= 0 ? "+" : "−"}$${formatNumber(Math.abs(userPnl || 0))}`} color={(userPnl || 0) >= 0 ? "text-emerald-400" : "text-rose-400"} />
                    <div className="h-px bg-[#1F1F24] my-1" />
                    <Row label="Penalty → counterparty (2% margin)" value={`−$${formatNumber(feeToCounter)}`} color="text-rose-400" />
                    <Row label="Penalty → Verslan (1% margin)" value={`−$${formatNumber(feeToProtocol)}`} color="text-rose-400" />
                    <div className="h-px bg-[#1F1F24] my-1" />
                    <Row label="Net impact" value={`${netImpact >= 0 ? "+" : "−"}$${formatNumber(Math.abs(netImpact))}`} color={netImpact >= 0 ? "text-emerald-400" : "text-rose-400"} bold />
                </div>
                <div className="grid grid-cols-2 gap-2 p-4 border-t border-[#1F1F24]">
                    <button
                        data-testid="exit-cancel-button"
                        onClick={onCancel}
                        className="py-2.5 border border-[#1F1F24] hover:border-zinc-600 text-zinc-300 text-xs uppercase tracking-widest font-semibold rounded-sm"
                    >
                        Keep position
                    </button>
                    <button
                        data-testid="exit-confirm-button"
                        onClick={onConfirm}
                        className="py-2.5 bg-amber-500 hover:bg-amber-400 text-black text-xs uppercase tracking-widest font-semibold rounded-sm"
                    >
                        Exit now →
                    </button>
                </div>
            </div>
        </div>
    );
}

const Row = ({ label, value, color = "text-zinc-100", bold = false }) => (
    <div className="flex items-center justify-between">
        <span className="text-zinc-500">{label}</span>
        <span className={`${color} ${bold ? "text-base font-semibold" : ""}`}>{value}</span>
    </div>
);

function AddMarginModal({ swap, userId, onCancel, onConfirm }) {
    const [amount, setAmount] = useState("50");
    if (!swap) return null;
    const userSide = swap.user_side || (swap.bull_user_id === userId ? "BULL" : "BEAR");
    const currentMargin = userSide === "BULL" ? swap.margin_bull : swap.margin_bear;
    const initial = userSide === "BULL" ? (swap.initial_margin_bull ?? currentMargin) : (swap.initial_margin_bear ?? currentMargin);
    const currentVariable = Math.max(0, currentMargin - initial);
    const add = parseFloat(amount) || 0;
    const newMargin = currentMargin + add;
    const pnl = userSide === "BULL" ? swap.bull_pnl : swap.bear_pnl;
    const newThreshold = newMargin > 0 ? Math.max(0, Math.min(100, ((newMargin + (pnl || 0)) / newMargin) * 100)) : 0;
    const oldThreshold = currentMargin > 0 ? Math.max(0, Math.min(100, ((currentMargin + (pnl || 0)) / currentMargin) * 100)) : 0;

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center px-4" data-testid="add-margin-modal">
            <div className="w-full max-w-md bg-[#0A0A0C] border border-[#1F1F24] rounded-sm">
                <div className="px-5 py-4 border-b border-[#1F1F24]">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">Variable margin</div>
                    <div className="text-lg font-medium mt-1">Add margin to {swap.coin} {userSide}</div>
                </div>
                <div className="p-5 grid gap-3 font-mono text-xs">
                    <Row label="Initial margin" value={`$${formatNumber(initial)}`} />
                    <Row label="Variable margin posted" value={`$${formatNumber(currentVariable)}`} color="text-cyan-400" />
                    <Row label="Current total margin" value={`$${formatNumber(currentMargin)}`} bold />
                    <Row label="Current threshold" value={`${oldThreshold.toFixed(1)}%`} color={oldThreshold >= 95 ? "text-emerald-400" : oldThreshold >= 80 ? "text-amber-300" : "text-rose-400"} />
                    <div className="h-px bg-[#1F1F24] my-1" />
                    <div className="grid gap-1.5">
                        <label className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">Add amount (USDC)</label>
                        <input
                            data-testid="add-margin-amount-input"
                            type="number" min={1}
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-base"
                        />
                    </div>
                    <Row label="New total margin" value={`$${formatNumber(newMargin)}`} color="text-cyan-400" bold />
                    <Row label="New threshold" value={`${newThreshold.toFixed(1)}%`} color={newThreshold >= 95 ? "text-emerald-400" : newThreshold >= 80 ? "text-amber-300" : "text-rose-400"} bold />
                </div>
                <div className="grid grid-cols-2 gap-2 p-4 border-t border-[#1F1F24]">
                    <button
                        data-testid="add-margin-cancel-button"
                        onClick={onCancel}
                        className="py-2.5 border border-[#1F1F24] hover:border-zinc-600 text-zinc-300 text-xs uppercase tracking-widest font-semibold rounded-sm"
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="add-margin-confirm-button"
                        onClick={() => onConfirm(amount)}
                        disabled={add <= 0}
                        className="py-2.5 bg-cyan-400 hover:bg-cyan-300 disabled:opacity-40 text-black text-xs uppercase tracking-widest font-semibold rounded-sm"
                    >
                        Lock +${formatNumber(add)} →
                    </button>
                </div>
            </div>
        </div>
    );
}

const Section = ({ title, children }) => (
    <div>
        <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400 mb-2">{title}</div>
        {children}
    </div>
);

const Table = ({ headers, children }) => (
    <div className="overflow-x-auto">
        <table className="w-full" data-testid="positions-table">
            <thead>
                <tr className="text-[9px] font-mono uppercase tracking-[0.22em] text-zinc-500">
                    {headers.map((h) => (
                        <th key={h} className="text-left py-1.5 px-2 font-normal">{h}</th>
                    ))}
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </table>
    </div>
);

/* ---------- Edit / Delete modals ---------- */

const DURATIONS = ["15m", "30m", "1h", "3h", "6h", "12h", "24h", "15d", "1mo", "1yr"];

function EditOrderModal({ order, onCancel, onConfirm }) {
    const [side, setSide] = useState(order?.side || "BULL");
    const [notional, setNotional] = useState(String(order?.notional || ""));
    const [margin, setMargin] = useState(String(order?.margin || ""));
    const [duration, setDuration] = useState(order?.duration_key || "15m");
    const [orderType, setOrderType] = useState(order?.order_type || "MARKET");
    const [limitPrice, setLimitPrice] = useState(order?.limit_price ? String(order.limit_price) : "");
    if (!order) return null;
    const submit = () => {
        const patch = {
            side, notional: parseFloat(notional), margin: parseFloat(margin),
            duration, order_type: orderType,
        };
        if (orderType === "LIMIT") patch.limit_price = parseFloat(limitPrice);
        onConfirm(patch);
    };
    return (
        <Backdrop onCancel={onCancel}>
            <div className="bg-[#0A0A0C] border border-cyan-500/30 rounded-sm p-6 w-full max-w-md grid gap-4"
                 data-testid="edit-order-modal" onClick={(e) => e.stopPropagation()}>
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">Edit open order</div>
                    <div className="text-base font-medium mt-1">{order.coin}{order.cross_coin ? `/${order.cross_coin}` : ""} · {order.id.slice(0, 8)}</div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-1">Atomic cancel + re-place. Margin is unlocked, then locked again with new params.</div>
                </div>
                <Seg label="Side" value={side} onChange={setSide}
                     opts={[{ v: "BULL", l: "BULL", c: "text-emerald-300" }, { v: "BEAR", l: "BEAR", c: "text-rose-300" }]} />
                <Seg label="Order type" value={orderType} onChange={setOrderType}
                     opts={[{ v: "MARKET", l: "MARKET" }, { v: "LIMIT", l: "LIMIT" }]} />
                <NumField label="Notional (USDC)" testid="edit-notional" value={notional} onChange={setNotional} />
                <NumField label="Margin (USDC)" testid="edit-margin" value={margin} onChange={setMargin} />
                {orderType === "LIMIT" && (
                    <NumField label="Limit price" testid="edit-limit-price" value={limitPrice} onChange={setLimitPrice} />
                )}
                <SelField label="Duration" testid="edit-duration" value={duration} onChange={setDuration} options={DURATIONS} />
                <div className="flex justify-end gap-2 pt-2">
                    <button data-testid="edit-order-cancel" onClick={onCancel} className="px-4 py-2 text-xs uppercase tracking-widest font-mono text-zinc-400 hover:text-zinc-100">Cancel</button>
                    <button data-testid="edit-order-confirm" onClick={submit} className="px-5 py-2 bg-cyan-400 hover:bg-cyan-300 text-black text-xs uppercase tracking-widest font-semibold rounded-sm">
                        Update order
                    </button>
                </div>
            </div>
        </Backdrop>
    );
}

function DeleteOrderModal({ order, onCancel, onConfirm }) {
    if (!order) return null;
    return (
        <Backdrop onCancel={onCancel}>
            <div className="bg-[#0A0A0C] border border-rose-500/40 rounded-sm p-6 w-full max-w-md grid gap-4"
                 data-testid="delete-order-modal" onClick={(e) => e.stopPropagation()}>
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-rose-400">Delete open order</div>
                    <div className="text-base font-medium mt-1">{order.side} · {order.coin}{order.cross_coin ? `/${order.cross_coin}` : ""} · {order.notional.toLocaleString()} notional</div>
                </div>
                <div className="text-[12px] font-mono text-zinc-300 leading-relaxed border border-emerald-500/30 bg-emerald-500/5 p-3 rounded-sm">
                    Full margin <span className="text-emerald-300">${order.margin.toLocaleString()}</span> is returned to your free balance. No fee, no penalty.
                </div>
                <div className="flex justify-end gap-2">
                    <button data-testid="delete-order-cancel" onClick={onCancel} className="px-4 py-2 text-xs uppercase tracking-widest font-mono text-zinc-400 hover:text-zinc-100">Keep order</button>
                    <button data-testid="delete-order-confirm" onClick={onConfirm} className="px-5 py-2 bg-rose-500 hover:bg-rose-400 text-white text-xs uppercase tracking-widest font-semibold rounded-sm">
                        Delete order
                    </button>
                </div>
            </div>
        </Backdrop>
    );
}

function EditPositionModal({ swap, userId, onCancel, onSubmit }) {
    const [tab, setTab] = useState("add"); // add | reduce | partial
    const [amount, setAmount] = useState("");
    const [fraction, setFraction] = useState("0.5");
    if (!swap) return null;
    const isBull = swap.bull_user_id === userId;
    const userMargin = isBull ? swap.margin_bull : swap.margin_bear;
    const userPnl = isBull ? swap.bull_pnl : swap.bear_pnl;
    const mark = swap.current_price || swap.entry_price;
    const submit = () => {
        if (tab === "add" || tab === "reduce") onSubmit(tab, { amount });
        else onSubmit("partial", { fraction });
    };
    return (
        <Backdrop onCancel={onCancel}>
            <div className="bg-[#0A0A0C] border border-cyan-500/30 rounded-sm p-6 w-full max-w-lg grid gap-4"
                 data-testid="edit-position-modal" onClick={(e) => e.stopPropagation()}>
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">Edit position</div>
                    <div className="text-base font-medium mt-1">{swap.coin}{swap.cross_coin ? `/${swap.cross_coin}` : ""} · {isBull ? "BULL" : "BEAR"} · {swap.id.slice(0, 8)}</div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-1">
                        Notional <span className="text-zinc-300">${swap.notional.toLocaleString()}</span> ·
                        Margin <span className="text-zinc-300">${userMargin.toFixed(2)}</span> ·
                        Mark <span className="text-cyan-300">{formatPrice(mark)}</span> ·
                        Unrealised <span className={userPnl >= 0 ? "text-emerald-300" : "text-rose-300"}>{userPnl >= 0 ? "+" : ""}{(userPnl || 0).toFixed(2)}</span>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-1 text-[10px] font-mono uppercase tracking-widest">
                    {[
                        { v: "add", l: "Add margin", testid: "edit-tab-add" },
                        { v: "reduce", l: "Reduce margin", testid: "edit-tab-reduce" },
                        { v: "partial", l: "Partial close", testid: "edit-tab-partial" },
                    ].map((t) => (
                        <button key={t.v} data-testid={t.testid} onClick={() => setTab(t.v)}
                                className={`py-2 rounded-sm border transition-colors ${tab === t.v ? "border-cyan-400 bg-cyan-500/10 text-cyan-300" : "border-[#1F1F24] text-zinc-500 hover:text-zinc-200"}`}>
                            {t.l}
                        </button>
                    ))}
                </div>

                {tab === "add" && (
                    <>
                        <NumField label="Amount to add (USDC)" testid="edit-add-amount" value={amount} onChange={setAmount} />
                        <div className="text-[10px] font-mono text-zinc-500 leading-relaxed">
                            Extra margin lowers your liquidation risk. Comes out of your free balance and locks until settlement or close.
                        </div>
                    </>
                )}
                {tab === "reduce" && (
                    <>
                        <NumField label="Amount to reduce (USDC)" testid="edit-reduce-amount" value={amount} onChange={setAmount} />
                        <div className="text-[10px] font-mono text-zinc-500 leading-relaxed border border-amber-500/30 bg-amber-500/5 p-2 rounded-sm">
                            Released back to free balance. Capped at the per-duration min-margin floor — if your reduction would dip below it, the request is rejected.
                        </div>
                    </>
                )}
                {tab === "partial" && (
                    <>
                        <NumField label="Fraction to close (0 – 1)" testid="edit-partial-fraction" value={fraction} onChange={setFraction} step="0.05" />
                        <div className="grid grid-cols-4 gap-1">
                            {[0.25, 0.5, 0.75, 0.9].map((f) => (
                                <button key={f} data-testid={`edit-partial-preset-${f}`} onClick={() => setFraction(String(f))}
                                        className="text-[10px] font-mono py-1.5 border border-[#1F1F24] hover:border-cyan-400 text-zinc-300 rounded-sm">
                                    {(f * 100).toFixed(0)}%
                                </button>
                            ))}
                        </div>
                        <div className="text-[10px] font-mono text-zinc-500 leading-relaxed border border-cyan-500/30 bg-cyan-500/5 p-2 rounded-sm">
                            Closes a slice at the current oracle mark and realises pro-rata PnL. Remaining swap keeps running with scaled-down notional + margin on both sides. No early-exit penalty.
                        </div>
                    </>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <button data-testid="edit-position-cancel" onClick={onCancel} className="px-4 py-2 text-xs uppercase tracking-widest font-mono text-zinc-400 hover:text-zinc-100">Cancel</button>
                    <button data-testid="edit-position-confirm" onClick={submit} className="px-5 py-2 bg-cyan-400 hover:bg-cyan-300 text-black text-xs uppercase tracking-widest font-semibold rounded-sm">
                        {tab === "add" ? "Add margin" : tab === "reduce" ? "Reduce margin" : "Partial close"}
                    </button>
                </div>
            </div>
        </Backdrop>
    );
}

function Backdrop({ children, onCancel }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4"
             onClick={onCancel}>
            {children}
        </div>
    );
}
function Seg({ label, value, onChange, opts }) {
    return (
        <div className="grid gap-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
            <div className="grid grid-cols-2 gap-1">
                {opts.map((o) => (
                    <button key={o.v} onClick={() => onChange(o.v)}
                            className={`py-2 text-[11px] font-mono uppercase tracking-widest border rounded-sm ${
                                value === o.v ? `border-cyan-400 bg-cyan-500/10 ${o.c || "text-cyan-300"}` : "border-[#1F1F24] text-zinc-500 hover:text-zinc-200"
                            }`}>
                        {o.l}
                    </button>
                ))}
            </div>
        </div>
    );
}
function NumField({ label, value, onChange, testid, step }) {
    return (
        <div className="grid gap-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
            <input type="number" min={0} step={step || "any"} data-testid={testid}
                   value={value} onChange={(e) => onChange(e.target.value)}
                   className="bg-transparent border-b border-[#1F1F24] focus:border-cyan-400 outline-none py-2 font-mono text-sm text-zinc-100 tabular-nums" />
        </div>
    );
}
function SelField({ label, value, onChange, options, testid }) {
    return (
        <div className="grid gap-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500">{label}</div>
            <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
                    className="bg-[#050507] border border-[#1F1F24] focus:border-cyan-400 rounded-sm py-2 px-3 font-mono text-sm text-zinc-100 outline-none">
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}


function OrderLineageModal({ orderId, onClose }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        api.get(`/swaps/orders/${orderId}/lineage`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.data?.detail || e.message));
    }, [orderId]);

    return (
        <Backdrop onCancel={onClose}>
            <div className="bg-[#0A0A0C] border border-cyan-500/30 rounded-sm p-6 w-full max-w-2xl grid gap-4"
                 data-testid="order-lineage-modal" onClick={(e) => e.stopPropagation()}>
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400">Edit history</div>
                    <div className="text-base font-medium mt-1 font-mono">{orderId.slice(0, 8)}…</div>
                    <div className="text-[10px] font-mono text-zinc-500 mt-1">
                        Each PATCH to an order creates a new revision. The original is marked REPLACED with a forward link to the new one.
                    </div>
                </div>
                {err && <div className="text-rose-300 text-[12px] font-mono">{err}</div>}
                {!data && !err && <div className="text-zinc-500 text-[12px] font-mono">Loading…</div>}
                {data && (
                    <div className="grid gap-2 max-h-[60vh] overflow-y-auto">
                        {data.revisions.map((r, i) => (
                            <div key={r.id}
                                 className={`border rounded-sm p-3 ${
                                     i === data.revisions.length - 1
                                         ? "border-cyan-400/60 bg-cyan-500/5"
                                         : "border-[#1F1F24] bg-[#050505]"
                                 }`}
                                 data-testid={`lineage-revision-${i}`}>
                                <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border border-cyan-500/40 bg-cyan-500/10 text-cyan-300">
                                            Rev {r.revision}
                                        </span>
                                        <span className="font-mono text-xs text-zinc-200">{r.id.slice(0, 8)}…</span>
                                        <LineageStatusPill status={r.status} />
                                    </div>
                                    <span className="font-mono text-[10px] text-zinc-500">
                                        {(r.created_at || "").replace("T", " ").slice(0, 19)} UTC
                                    </span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-3 font-mono text-[11px]">
                                    <Detail label="Side" value={r.side} accent={r.side === "BULL" ? "text-emerald-300" : "text-rose-300"} />
                                    <Detail label="Pair" value={`${r.coin}${r.cross_coin ? `/${r.cross_coin}` : ""}`} />
                                    <Detail label="Notional" value={r.notional.toLocaleString()} />
                                    <Detail label="Margin" value={r.margin.toLocaleString()} />
                                    <Detail label="Duration" value={r.duration_key} />
                                    <Detail label="Type" value={`${r.order_type}${r.limit_price ? ` @ ${r.limit_price}` : ""}`} />
                                    {r.replaced_at && (
                                        <Detail label="Replaced at" value={r.replaced_at.replace("T", " ").slice(0, 19)} />
                                    )}
                                    {r.swap_id && (
                                        <Detail label="Matched swap" value={`${r.swap_id.slice(0, 8)}…`} accent="text-cyan-300" />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex justify-end pt-2">
                    <button data-testid="lineage-close" onClick={onClose}
                            className="px-4 py-2 text-xs uppercase tracking-widest font-mono text-zinc-400 hover:text-zinc-100">
                        Close
                    </button>
                </div>
            </div>
        </Backdrop>
    );
}

function LineageStatusPill({ status }) {
    const theme = {
        OPEN:          "border-cyan-500/40 bg-cyan-500/5 text-cyan-300",
        LIMIT_PENDING: "border-amber-500/40 bg-amber-500/5 text-amber-300",
        MATCHED:       "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
        REPLACED:      "border-zinc-700 text-zinc-500",
        CANCELLED:     "border-zinc-700 text-zinc-500",
    }[status] || "border-zinc-700 text-zinc-400";
    return (
        <span className={`text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm border ${theme}`}>
            {status}
        </span>
    );
}
function Detail({ label, value, accent = "text-zinc-200" }) {
    return (
        <div>
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-zinc-500">{label}</div>
            <div className={`mt-0.5 ${accent}`}>{value}</div>
        </div>
    );
}

